<template>
  <div class="space-y-6">

    <!-- Section 1: Meta Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

      <!-- Meta Diaria -->
      <div class="rounded-2xl border border-gray-800 bg-gray-900/80 p-5 shadow-lg">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-bold uppercase tracking-wider text-emerald-400">Meta Diaria</span>
          <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            :class="dailyOnTrack ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'">
            {{ dailyOnTrack ? 'No alvo' : 'Abaixo' }}
          </span>
        </div>
        <div class="text-2xl font-black text-white mb-1">{{ summary.currentDayPct?.toFixed(2) ?? '0.00' }}%</div>
        <div class="text-xs text-gray-400 mb-3">Meta: {{ config.dailyTargetPct }}%/dia</div>
        <div class="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden">
          <div class="h-full rounded-full transition-all duration-500"
            :class="dailyOnTrack ? 'bg-gradient-to-r from-emerald-500 to-green-400' : 'bg-gradient-to-r from-red-500 to-orange-400'"
            :style="{ width: Math.min(dailyProgress, 100) + '%' }"></div>
        </div>
      </div>

      <!-- Meta 30 Dias -->
      <div class="rounded-2xl border border-gray-800 bg-gray-900/80 p-5 shadow-lg">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-bold uppercase tracking-wider text-emerald-400">Meta 30 Dias</span>
          <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            {{ thirtyDayProgress.toFixed(0) }}%
          </span>
        </div>
        <div class="text-2xl font-black text-white mb-1">${{ formatCurrency(summary.thirtyDayGain ?? 0) }}</div>
        <div class="text-xs text-gray-400 mb-3">Meta: ${{ formatCurrency(config.thirtyDayTarget) }}</div>
        <div class="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden">
          <div class="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
            :style="{ width: Math.min(thirtyDayProgress, 100) + '%' }"></div>
        </div>
      </div>

      <!-- Streak -->
      <div class="rounded-2xl border border-gray-800 bg-gray-900/80 p-5 shadow-lg">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-bold uppercase tracking-wider text-emerald-400">Streak</span>
          <span class="text-lg">&#128293;</span>
        </div>
        <div class="text-2xl font-black text-white mb-1">{{ summary.streak ?? 0 }} dias</div>
        <div class="text-xs text-gray-400">Dias lucrativos consecutivos</div>
        <div class="mt-3 flex gap-1">
          <div v-for="i in Math.min(summary.streak || 0, 10)" :key="i"
            class="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50"></div>
          <div v-for="i in Math.max(10 - (summary.streak || 0), 0)" :key="'e' + i"
            class="w-2.5 h-2.5 rounded-full bg-gray-700"></div>
        </div>
      </div>

      <!-- Projecao 30 Dias -->
      <div class="rounded-2xl border border-gray-800 bg-gray-900/80 p-5 shadow-lg">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-bold uppercase tracking-wider text-emerald-400">Projecao 30 Dias</span>
          <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
            Estimado
          </span>
        </div>
        <div class="text-2xl font-black text-white mb-1">${{ formatCurrency(projectedBalance) }}</div>
        <div class="text-xs text-gray-400">Baseado na media de {{ avgDailyReturn.toFixed(2) }}%/dia</div>
        <div class="text-xs text-emerald-400 mt-2 font-semibold">
          +${{ formatCurrency(projectedBalance - (summary.currentBalance ?? 0)) }} estimado
        </div>
      </div>
    </div>

    <!-- Section 2: Tabs Realizado / Projeção -->
    <div class="rounded-2xl border border-gray-800 bg-gray-900/80 shadow-lg overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button @click="metasTab = 'realizado'" class="px-4 py-1.5 text-sm font-bold rounded-lg transition"
            :class="metasTab === 'realizado' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-gray-500 hover:text-gray-300'">
            📊 Realizado
          </button>
          <button @click="metasTab = 'projecao'" class="px-4 py-1.5 text-sm font-bold rounded-lg transition"
            :class="metasTab === 'projecao' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'text-gray-500 hover:text-gray-300'">
            🔮 Projeção
          </button>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="loadingHistory" class="text-xs text-gray-500 animate-pulse">Atualizando...</span>
          <span class="text-[10px] text-gray-500">Atualiza a cada 15s</span>
        </div>
      </div>
      <!-- Tab: Realizado ou Projeção - tabela compartilhada -->
      <div v-if="metasTab === 'realizado' || metasTab === 'projecao'" class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-800">
              <th class="px-4 py-3 text-left font-semibold">Dia</th>
              <th class="px-4 py-3 text-left font-semibold">Data</th>
              <th class="px-4 py-3 text-right font-semibold">Saldo Inicial</th>
              <th class="px-4 py-3 text-right font-semibold">Lucro $</th>
              <th class="px-4 py-3 text-right font-semibold">Lucro %</th>
              <th class="px-4 py-3 text-right font-semibold">Saldo Final</th>
              <th class="px-4 py-3 text-center font-semibold">Meta %</th>
              <th class="px-4 py-3 text-center font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in (metasTab === 'realizado' ? evolutionRows.filter(r => !r.projected) : evolutionRows.filter(r => r.projected))" :key="row.day"
              class="border-b border-gray-800/50 transition-colors"
              :class="row.projected ? 'opacity-50 italic' : 'hover:bg-gray-800/40'">
              <td class="px-4 py-2.5 text-gray-300 font-mono">{{ row.day }}</td>
              <td class="px-4 py-2.5 text-gray-400">{{ row.date }}</td>
              <td class="px-4 py-2.5 text-right text-gray-300 font-mono">${{ formatCurrency(row.startBalance) }}</td>
              <td class="px-4 py-2.5 text-right font-mono font-semibold"
                :class="row.profit >= 0 ? 'text-emerald-400' : 'text-red-400'">
                {{ row.profit >= 0 ? '+' : '' }}${{ formatCurrency(row.profit) }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono"
                :class="row.profitPct >= 0 ? 'text-emerald-400' : 'text-red-400'">
                {{ row.profitPct >= 0 ? '+' : '' }}{{ row.profitPct.toFixed(2) }}%
              </td>
              <td class="px-4 py-2.5 text-right text-white font-mono font-semibold">${{ formatCurrency(row.endBalance) }}</td>
              <td class="px-4 py-2.5 text-center text-gray-400 font-mono">{{ config.dailyTargetPct }}%</td>
              <td class="px-4 py-2.5 text-center text-lg">
                <span v-if="row.projected" class="text-gray-600">--</span>
                <span v-else-if="row.profitPct >= config.dailyTargetPct" class="text-emerald-400">&#10004;</span>
                <span v-else class="text-red-400">&#10008;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Painel AI Projeção (só na aba projeção) -->
      <div v-if="metasTab === 'projecao'" class="p-5 border-t border-gray-800">
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <label class="text-sm text-gray-400 font-semibold whitespace-nowrap">Dias de projecao:</label>
          <input type="range" v-model.number="projectionDays" min="1" max="10" step="1"
            class="w-full sm:w-64 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500" />
          <span class="text-lg font-black text-emerald-400 min-w-[3rem] text-center">{{ projectionDays }}</span>
        </div>

        <div v-if="aiProjection" class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Pessimista -->
          <div class="rounded-xl border border-red-500/30 bg-red-950/20 p-4">
            <div class="text-xs font-bold uppercase tracking-wider text-red-400 mb-2">Pessimista</div>
            <div class="text-xl font-black text-white">${{ formatCurrency(aiProjection.pessimistic?.balance ?? 0) }}</div>
            <div class="text-xs mt-1" :class="(aiProjection.pessimistic?.changePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'">
              {{ (aiProjection.pessimistic?.changePct ?? 0) >= 0 ? '+' : '' }}{{ (aiProjection.pessimistic?.changePct ?? 0).toFixed(2) }}%
            </div>
            <div class="text-[10px] text-gray-500 mt-1">em {{ projectionDays }} dia(s)</div>
          </div>

          <!-- Esperado -->
          <div class="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4">
            <div class="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-2">Esperado</div>
            <div class="text-xl font-black text-white">${{ formatCurrency(aiProjection.expected?.balance ?? 0) }}</div>
            <div class="text-xs mt-1" :class="(aiProjection.expected?.changePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'">
              {{ (aiProjection.expected?.changePct ?? 0) >= 0 ? '+' : '' }}{{ (aiProjection.expected?.changePct ?? 0).toFixed(2) }}%
            </div>
            <div class="text-[10px] text-gray-500 mt-1">em {{ projectionDays }} dia(s)</div>
          </div>

          <!-- Otimista -->
          <div class="rounded-xl border border-cyan-500/30 bg-cyan-950/20 p-4">
            <div class="text-xs font-bold uppercase tracking-wider text-cyan-400 mb-2">Otimista</div>
            <div class="text-xl font-black text-white">${{ formatCurrency(aiProjection.optimistic?.balance ?? 0) }}</div>
            <div class="text-xs mt-1" :class="(aiProjection.optimistic?.changePct ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'">
              {{ (aiProjection.optimistic?.changePct ?? 0) >= 0 ? '+' : '' }}{{ (aiProjection.optimistic?.changePct ?? 0).toFixed(2) }}%
            </div>
            <div class="text-[10px] text-gray-500 mt-1">em {{ projectionDays }} dia(s)</div>
          </div>
        </div>

        <div v-else class="text-center py-8 text-gray-500 text-sm">
          Selecione os dias para gerar a projecao com IA
        </div>
      </div>
    </div><!-- end tabs container -->

    <!-- Link para configurações -->
    <div class="rounded-2xl border border-gray-800 bg-gray-900/80 p-4 flex items-center justify-between">
      <div>
        <p class="text-sm text-white font-medium">Configuracoes de Metas</p>
        <p class="text-xs text-gray-500">Meta diaria: {{ config.dailyTargetPct }}% | Meta 30 dias: ${{ formatCurrency(config.thirtyDayTarget || 0) }}</p>
      </div>
      <router-link to="/settings" class="px-4 py-2 bg-emerald-600/20 text-emerald-400 text-sm font-medium rounded-lg hover:bg-emerald-600/30 transition">
        Configurar
      </router-link>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { getApiV1BaseUrl } from '../config/runtime';

// --- State ---
const metasTab = ref('realizado');
const summary = ref({});
const history = ref([]);
const aiProjection = ref(null);
const projectionDays = ref(5);
const settingsOpen = ref(false);
const configSaved = ref(false);
const loadingHistory = ref(false);
const loadingProjection = ref(false);
const savingConfig = ref(false);

const config = ref({
  dailyTargetPct: 5,
  thirtyDayTarget: 500,
});

const configForm = ref({
  dailyTargetPct: 5,
  thirtyDayTarget: 500,
});

let historyInterval = null;
let projectionInterval = null;

// --- Auth helpers ---
function getHeaders(json = false) {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  const h = {};
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function formatCurrency(val) {
  if (val == null || isNaN(val)) return '0.00';
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// --- Computed ---
const dailyOnTrack = computed(() => (summary.value.currentDayPct ?? 0) >= config.value.dailyTargetPct);

const dailyProgress = computed(() => {
  if (!config.value.dailyTargetPct) return 0;
  return ((summary.value.currentDayPct ?? 0) / config.value.dailyTargetPct) * 100;
});

const thirtyDayProgress = computed(() => {
  if (!config.value.thirtyDayTarget) return 0;
  return ((summary.value.thirtyDayGain ?? 0) / config.value.thirtyDayTarget) * 100;
});

const avgDailyReturn = computed(() => {
  const real = history.value.filter(d => d.profitPct != null);
  if (!real.length) return config.value.dailyTargetPct;
  return real.reduce((sum, d) => sum + d.profitPct, 0) / real.length;
});

const projectedBalance = computed(() => {
  const balance = summary.value.currentBalance ?? 0;
  const daysLeft = 30 - (history.value.length || 0);
  if (daysLeft <= 0) return balance;
  return balance * Math.pow(1 + avgDailyReturn.value / 100, daysLeft);
});

const calculatedThirtyDay = computed(() => {
  const balance = summary.value.currentBalance ?? 0;
  const projected = balance * Math.pow(1 + configForm.value.dailyTargetPct / 100, 30);
  return projected - balance;
});

const evolutionRows = computed(() => {
  const rows = [];
  const realDays = history.value || [];
  const today = new Date();

  // Real data rows
  for (let i = 0; i < realDays.length; i++) {
    const d = realDays[i];
    rows.push({
      day: i + 1,
      date: d.date || formatDate(today, i - realDays.length + 1),
      startBalance: d.startBalance ?? 0,
      profit: d.profit ?? 0,
      profitPct: d.profitPct ?? 0,
      endBalance: d.endBalance ?? 0,
      projected: false,
    });
  }

  // Projected rows to fill up to 30
  const lastBalance = realDays.length > 0
    ? (realDays[realDays.length - 1].endBalance ?? 0)
    : (summary.value.currentBalance ?? 0);
  const avgPct = avgDailyReturn.value;

  for (let i = realDays.length; i < 30; i++) {
    const startBal = i === realDays.length ? lastBalance : rows[i - 1].endBalance;
    const profit = startBal * (avgPct / 100);
    rows.push({
      day: i + 1,
      date: formatDate(today, i - realDays.length + 1),
      startBalance: startBal,
      profit: profit,
      profitPct: avgPct,
      endBalance: startBal + profit,
      projected: true,
    });
  }

  return rows;
});

// --- Helpers ---
function formatDate(base, offsetDays) {
  const d = new Date(base);
  d.setDate(d.getDate() + offsetDays);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// --- API calls ---
async function fetchSummary() {
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/metas/history`, { headers: getHeaders() });
    if (res.ok) {
      const data = await res.json();
      const hist = data.history || [];
      const todayRow = hist.length > 0 ? hist[hist.length - 1] : {};
      summary.value = {
        currentBalance: data.currentBalance || 0,
        initialBalance: data.initialBalance || 100,
        totalProfit: data.totalProfit || 0,
        totalProfitPct: data.totalProfitPct || 0,
        avgDailyPct: data.avgDailyPct || 0,
        daysActive: data.daysActive || 0,
        streak: data.streak || 0,
        currentDayPct: todayRow.profitPct || 0,
        thirtyDayGain: data.totalProfit || 0,
      };
    }
  } catch (e) {
    console.error('[Metas] Erro ao buscar resumo:', e);
  }
}

async function fetchHistory() {
  loadingHistory.value = true;
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/metas/history`, { headers: getHeaders() });
    if (res.ok) {
      const data = await res.json();
      history.value = Array.isArray(data) ? data : (data.history || data.days || []);
    }
  } catch (e) {
    console.error('[Metas] Erro ao buscar historico:', e);
  } finally {
    loadingHistory.value = false;
  }
}

async function fetchConfig() {
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/metas/config`, { headers: getHeaders() });
    if (res.ok) {
      const data = await res.json();
      config.value = { ...config.value, ...data };
      configForm.value = { ...configForm.value, ...data };
    }
  } catch (e) {
    console.error('[Metas] Erro ao buscar config:', e);
  }
}

async function fetchProjection() {
  loadingProjection.value = true;
  try {
    const res = await fetch(
      `${getApiV1BaseUrl()}/metas/projection?days=${projectionDays.value}`,
      { headers: getHeaders() }
    );
    if (res.ok) {
      aiProjection.value = await res.json();
    }
  } catch (e) {
    console.error('[Metas] Erro ao buscar projecao IA:', e);
  } finally {
    loadingProjection.value = false;
  }
}

async function saveConfig() {
  savingConfig.value = true;
  configSaved.value = false;
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/metas/config`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(configForm.value),
    });
    if (res.ok) {
      const data = await res.json();
      config.value = { ...config.value, ...data };
      configSaved.value = true;
      setTimeout(() => { configSaved.value = false; }, 3000);
    }
  } catch (e) {
    console.error('[Metas] Erro ao salvar config:', e);
  } finally {
    savingConfig.value = false;
  }
}

// --- Watchers ---
watch(projectionDays, () => {
  fetchProjection();
});

// --- Lifecycle ---
onMounted(async () => {
  await Promise.all([fetchSummary(), fetchHistory(), fetchConfig()]);
  fetchProjection();

  // Auto-refresh: history + summary every 15s
  historyInterval = setInterval(() => {
    fetchSummary();
    fetchHistory();
  }, 15000);

  // Auto-refresh: AI projection every 30s
  projectionInterval = setInterval(() => {
    fetchProjection();
  }, 30000);
});

onUnmounted(() => {
  if (historyInterval) clearInterval(historyInterval);
  if (projectionInterval) clearInterval(projectionInterval);
});
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 500px;
  opacity: 1;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #10b981;
  cursor: pointer;
  border: 2px solid #064e3b;
  box-shadow: 0 0 6px rgba(16, 185, 129, 0.4);
}

input[type="range"]::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #10b981;
  cursor: pointer;
  border: 2px solid #064e3b;
  box-shadow: 0 0 6px rgba(16, 185, 129, 0.4);
}
</style>
